import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  GitBranch,
  ArrowLeft,
  AlertCircle,
  Loader2,
  Wand2,
} from 'lucide-react';
import {
  IterationGraphView,
  IterationNodeDetail,
} from '../baselines/image/iteration';
import { iterationAPI, type IterationGraphNode } from '../baselines/image/iteration/api';
import { api } from '../services/api';
import { formatBeijingDate } from '../utils/time';
import { useSiteName } from '../platform/useSiteName';

export default function IterationHistoryPage() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const [rootNodes, setRootNodes] = useState<IterationGraphNode[]>([]);
  const [allNodes, setAllNodes] = useState<Map<string, IterationGraphNode>>(new Map());
  const [selectedNode, setSelectedNode] = useState<IterationGraphNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ totalChains: 0, totalNodes: 0, maxDepth: 0 });

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const graph = await iterationAPI.getIterationGraph();
        const nodeMap = new Map<string, IterationGraphNode>();
        graph.nodes.forEach((node) => nodeMap.set(node.id, node));

        // 计算最大深度
        let maxDepth = 0;
        graph.nodes.forEach((node) => {
          if (node.generation > maxDepth) maxDepth = node.generation;
        });

        setAllNodes(nodeMap);
        setRootNodes(graph.rootNodes);
        setStats({
          totalChains: graph.rootNodes.length,
          totalNodes: graph.nodes.length,
          maxDepth,
        });
      } catch (error) {
        console.error('Failed to load iteration graph:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // 使用节点作为参考图继续迭代
  const handleUseAsReference = (node: IterationGraphNode) => {
    navigate('/', {
      state: {
        continueIteration: true,
        jobId: node.jobId,
        imageUrl: node.imageUrl,
        prompt: node.prompt,
      },
    });
  };

  const user = api.getSavedUser();

  const isLoggedIn = !!user;

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-light-bg" style={{ paddingTop: '64px' }}>
        <nav className="bg-white/80 backdrop-blur-md border-b border-light-border shadow-sm fixed top-0 left-0 right-0 z-[9999]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{siteName}</span>
              </button>
            </div>
          </div>
        </nav>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <GitBranch className="w-16 h-16 text-light-muted mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-light-text mb-2">迭代记录</h1>
            <p className="text-light-secondary mb-6">请登录后查看您的图片迭代历史</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              返回首页
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg" style={{ paddingTop: '64px' }}>
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-light-border shadow-sm fixed top-0 left-0 right-0 z-[9999]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-semibold">返回</span>
              </button>
              <span className="text-light-muted">/</span>
              <span className="font-medium text-light-text">迭代记录</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-light-text mb-2">图片迭代记录</h1>
          <p className="text-light-secondary max-w-xl mx-auto">
            追踪您的创作历程，查看从初始图片到最终版本的完整迭代关系图谱
          </p>
        </div>

        {/* 统计卡片 */}
        {!isLoading && rootNodes.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-accent-blue">{stats.totalChains}</div>
              <div className="text-xs text-light-muted">迭代链</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-accent-blue">{stats.totalNodes}</div>
              <div className="text-xs text-light-muted">总节点</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-accent-blue">{stats.maxDepth}</div>
              <div className="text-xs text-light-muted">最大深度</div>
            </div>
          </div>
        )}

        {/* 加载状态 */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
          </div>
        )}

        {/* 空状态 */}
        {!isLoading && rootNodes.length === 0 && (
          <div className="card p-12 text-center">
            <GitBranch className="w-16 h-16 text-light-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-light-text mb-2">暂无迭代记录</h3>
            <p className="text-sm text-light-muted mb-6">
              开始生成图片并使用"作为参考图"功能，即可创建迭代链
            </p>
            <button onClick={() => navigate('/')} className="btn-primary flex items-center gap-2 mx-auto">
              <Wand2 className="w-4 h-4" />
              开始创作
            </button>
          </div>
        )}

        {/* 图谱视图 */}
        {!isLoading && rootNodes.length > 0 && (
          <div className="space-y-6">
            {rootNodes.map((root) => (
              <div key={root.id} className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-accent-blue" />
                    <span className="text-sm font-medium text-light-text">
                      迭代链 #{root.id.slice(-6)}
                    </span>
                    <span className="text-xs text-light-muted">
                      创建于 {formatBeijingDate(root.createdAt)}
                    </span>
                  </div>
                </div>
                <IterationGraphView
                  rootNodes={[root]}
                  allNodes={allNodes}
                  onNodeClick={setSelectedNode}
                  selectedNodeId={selectedNode?.id}
                />
              </div>
            ))}
          </div>
        )}

        {/* 提示信息 */}
        {!isLoading && rootNodes.length > 0 && (
          <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-800">使用提示</h4>
                <ul className="text-xs text-blue-700 space-y-1 mt-1">
                  <li>• 拖拽节点可调整布局，滚轮缩放画布</li>
                  <li>• 点击任意节点查看详情和参数</li>
                  <li>• 鼠标悬停显示提示词预览</li>
                  <li>• 迭代数据永久保存，记录您的完整创作历程</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* 节点详情弹窗 */}
      {selectedNode && (
        <IterationNodeDetail
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
          onUseAsReference={handleUseAsReference}
        />
      )}
    </div>
  );
}
